import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { homeworkAPI } from '../../services/api';
import PDFViewerModal from '../../components/PDFViewerModal';
import './Homework.css';

const Homework = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [assignments, setAssignments] = useState([]);
  const [submissionsMap, setSubmissionsMap] = useState({});
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState({});
  const [comments, setComments] = useState({});
  const [submissionModes, setSubmissionModes] = useState({});
  const [textSubmissions, setTextSubmissions] = useState({});
  
  // PDF Modal state
  const [pdfModal, setPdfModal] = useState({ isOpen: false, url: '', title: '' });

  const formatDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const fetchAssignments = async () => {
    try {
      const data = await homeworkAPI.getAll({ limit: 1000 });
      setAssignments(data.homework || []);
    } catch (err) {
      console.error('Load homework assignments', err);
    }
  };

  const fetchSubmissions = async (homeworkId) => {
    try {
      const submissions = await homeworkAPI.getSubmissions(homeworkId);
      setSubmissionsMap(prev => ({ ...prev, [homeworkId]: submissions }));
    } catch (err) {
      console.error('Load submissions', err);
    }
  };

  useEffect(() => {
    if (user) {
      fetchAssignments();
    }
  }, [user]);

  useEffect(() => {
    assignments.forEach((hw) => {
      if (!submissionsMap[hw.id]) {
        fetchSubmissions(hw.id);
      }
    });
  }, [assignments, submissionsMap]);

  const handleFileSubmit = async (homeworkId, file, comment, textContent, mode) => {
    const trimmedText = String(textContent || '').trim();
    if (mode === 'text' && !trimmedText) return;
    if (mode === 'document' && !file) return;

    setUploading(true);
    try {
      await homeworkAPI.submit(homeworkId, file, comment, mode === 'text' ? trimmedText : '');
      await fetchSubmissions(homeworkId);
      setSelectedFiles(prev => ({ ...prev, [homeworkId]: null }));
      setComments(prev => ({ ...prev, [homeworkId]: '' }));
      setTextSubmissions(prev => ({ ...prev, [homeworkId]: '' }));
    } catch (err) {
      console.error('Submit homework', err);
      alert(t('common.error'));
    } finally {
      setUploading(false);
    }
  };

  // PDF Modal handlers
  const openPdfModal = (url, title) => {
    setPdfModal({ isOpen: true, url, title });
  };

  const closePdfModal = () => {
    setPdfModal({ isOpen: false, url: '', title: '' });
  };

  const homeworkList = useMemo(() => {
    return assignments.map(hw => {
      const submissions = submissionsMap[hw.id] || [];
      return {
        ...hw,
        submissions
      };
    });
  }, [assignments, submissionsMap]);

  return (
    <div className="homework-page">
      <div className="page-header">
        <h1>{t('nav.homework')}</h1>
      </div>

      <div className="homework-list">
        {homeworkList.map((hw) => {
          const latestSubmission = hw.submissions?.[0];
          const status = latestSubmission ? 'submitted' : 'pending';

          return (
            <div key={hw.id} className="homework-card">
              <div className="homework-header">
                <h3>{hw.title}</h3>
                <span className={`status-badge ${status}`}>
                  {status === 'submitted' ? t('homework.submitted') : t('common.pending')}
                </span>
              </div>
              
              <p className="homework-description">{hw.description}</p>
              
              <div className="homework-meta">
                <div className="meta-item">
                  <span className="meta-label">{t('course.title')}:</span>
                  <span className="meta-value">{hw.course?.name}</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">{t('homework.dueDate')}:</span>
                  <span className="meta-value due-date">{formatDateTime(hw.dueDate)}</span>
                </div>
                {hw.module && (
                  <div className="meta-item">
                    <span className="meta-label">Module:</span>
                    <span className="meta-value">{hw.module?.code} - {hw.module?.name}</span>
                  </div>
                )}
              </div>

              {/* Content Display */}
              {hw.contentType === 'text' ? (
                <div className="homework-content">
                  <div className="content-header">
                    <span className="content-type">📝 Tekstueel huiswerk</span>
                  </div>
                  <div className="text-content">
                    {hw.content}
                  </div>
                </div>
              ) : (
                hw.attachment && (
                  <div className="homework-view">
                    <button 
                      className="view-link"
                      onClick={() => openPdfModal(homeworkAPI.viewAttachmentUrl(hw.id), hw.title)}
                    >
                      {t('homework.viewAssignment')} - {hw.attachment.originalName}
                    </button>
                  </div>
                )
              )}

              {latestSubmission && (
                <div className="submission-info">
                  <div className="submission-date">
                    {t('homework.submittedOn')}: {new Date(latestSubmission.submittedAt).toLocaleDateString()}
                  </div>
                  {latestSubmission.grade !== undefined && latestSubmission.grade !== null && (
                    <div className="grade-info">
                      <span className={`grade-badge ${latestSubmission.grade >= 5.5 ? 'passed' : 'failed'}`}>
                        {t('grade.grade')}: {latestSubmission.grade}/10
                      </span>
                    </div>
                  )}
                  {latestSubmission.feedback && (
                    <div className="feedback-info">
                      <strong>{t('homework.feedback')}:</strong> {latestSubmission.feedback}
                    </div>
                  )}
                  <button 
                    className="download-link"
                    onClick={() => openPdfModal(homeworkAPI.viewSubmissionUrl(hw.id, latestSubmission.id), `${hw.title} - ${t('homework.mySubmission')}`)}
                  >
                    {t('homework.viewSubmission')}
                  </button>
                </div>
              )}

              <div className="homework-actions">
                <div className="submission-mode-toggle">
                  <label>
                    <input
                      type="radio"
                      name={`submission-mode-${hw.id}`}
                      checked={(submissionModes[hw.id] || 'document') === 'document'}
                      onChange={() => setSubmissionModes(prev => ({ ...prev, [hw.id]: 'document' }))}
                    />
                    Document
                  </label>
                  <label>
                    <input
                      type="radio"
                      name={`submission-mode-${hw.id}`}
                      checked={(submissionModes[hw.id] || 'document') === 'text'}
                      onChange={() => setSubmissionModes(prev => ({ ...prev, [hw.id]: 'text' }))}
                    />
                    Tekst
                  </label>
                </div>

                {(submissionModes[hw.id] || 'document') === 'document' && (
                <label className="file-upload">
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={(e) => setSelectedFiles(prev => ({ ...prev, [hw.id]: e.target.files[0] }))}
                  />
                  {t('homework.selectFile')}
                </label>
                )}

                {(submissionModes[hw.id] || 'document') === 'text' && (
                  <textarea
                    className="comment-input"
                    placeholder="Typ je tekstinzending hier"
                    value={textSubmissions[hw.id] || ''}
                    onChange={(e) => setTextSubmissions(prev => ({ ...prev, [hw.id]: e.target.value }))}
                  />
                )}

                <textarea
                  className="comment-input"
                  placeholder={t('homework.commentsPlaceholder')}
                  value={comments[hw.id] || ''}
                  onChange={(e) => setComments(prev => ({ ...prev, [hw.id]: e.target.value }))}
                />

                <button
                  className="upload-btn"
                  onClick={() => handleFileSubmit(
                    hw.id,
                    selectedFiles[hw.id],
                    comments[hw.id],
                    textSubmissions[hw.id],
                    submissionModes[hw.id] || 'document'
                  )}
                  disabled={uploading || (((submissionModes[hw.id] || 'document') === 'document') ? !selectedFiles[hw.id] : !String(textSubmissions[hw.id] || '').trim())}
                >
                  {uploading ? t('common.loading') : t('homework.uploadFile')}
                </button>
              </div>

              {latestSubmission?.textContent && (
                <div className="submission-info">
                  <strong>Tekstinzending:</strong>
                  <div className="text-content">{latestSubmission.textContent}</div>
                </div>
              )}
            </div>
          );
        })}
        
        {homeworkList.length === 0 && (
          <div className="no-homework">
            <p>{t('common.noData')}</p>
          </div>
        )}
      </div>

      {/* PDF Viewer Modal */}
      <PDFViewerModal
        isOpen={pdfModal.isOpen}
        onClose={closePdfModal}
        pdfUrl={pdfModal.url}
        title={pdfModal.title}
      />
    </div>
  );
};

export default Homework;

